import { Box, Button, IconButton, Typography } from '@mui/material';
import React, { useCallback, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { GarmentContext } from '../Context/GarmentContext/GarmentContext';
import { UserContext } from '../Context/UserContext/UserContextProvider';
import { teesPropertiesObject } from '../tools/generalProperties';
import { TeeMeasureLabel } from '../types/databaseTables/idealteemeasures';
import ComparisonSlider from './ComparisonSlider';
import { FormContainer, PageContainer } from './Containers';
import HelpDialog from './HelpDialog/HelpDialog';
import { LoggingContext } from '../Context/LoggingContext/LoggingContext';

function ComparisonSlidersSectionHelpContents() {
  return (
    <Box>
      <p>
        The comparison sliders show you a breakdown of the garment&apos;s measurements and allows
        you to make a detailed comparison with your own ideal measurements.
      </p>
      <p>
        The garment measurement is detailed in <span style={{ color: 'darkblue' }}>blue</span>, and
        your ideal measurement is shown in <span style={{ color: 'darkgreen' }}>green</span>
      </p>
      <ComparisonSlider
        eachMeasureKey='chest'
        eachMeasureProperties={teesPropertiesObject.chest}
        id='demo-slider-chest'
        idealMeasurement={30}
        initialMeasure={31}
        key='demo-chest'
      />
      <p>Hovering/pressing the slider should provide details.</p>
      <p>
        If you have tried on this garment, you can use its measurements as a basis for your own
        ideal measurements by clicking the button below the sliders:
      </p>
      <br />
      <Button variant='contained' color='secondary' sx={{ display: 'block', margin: 'auto' }}>
        Make these my ideal measurements
      </Button>
    </Box>
  );
}

function ComparisonSlidersSection() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userDetailsState, dispatchUserDetailsState } = useContext(UserContext);
  const { garmentObjectState } = useContext(GarmentContext);
  const { addLog } = useContext(LoggingContext);
  const [openHelpDialog, setOpenHelpDialog] = useState(false);

  const ReturnButton = useCallback(
    () => (
      <Button
        sx={{
          display: 'block',
          margin: 'auto',
          width: '300px',
        }}
        variant='outlined'
        onClick={() => {
          addLog('back to garment page clicked', location.pathname);
          navigate(-1);
        }}
      >
        Back to Garment Page
      </Button>
    ),
    []
  );
  return (
    <PageContainer>
      <ReturnButton />
      <br />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 80px' }}>
        <Typography variant='h5' alignSelf='center'>
          Comparison Sliders:
        </Typography>
        <IconButton
          aria-label='more information'
          color='default'
          onClick={() => {
            addLog('open help dialog', location.pathname);
            setOpenHelpDialog(true);
          }}
          size='large'
        >
          <HelpOutlineIcon fontSize='large' />
        </IconButton>
      </Box>
      <HelpDialog
        headingTitle='Comparison Sliders'
        open={openHelpDialog}
        setOpen={setOpenHelpDialog}
      >
        <ComparisonSlidersSectionHelpContents />
      </HelpDialog>
      <FormContainer>
        {Object.keys(teesPropertiesObject).map((eachMeasureKey) => (
          <ComparisonSlider
            eachMeasureProperties={teesPropertiesObject[eachMeasureKey as TeeMeasureLabel]}
            eachMeasureKey={eachMeasureKey}
            key={eachMeasureKey}
            id={`garmentComparisonSlider-${eachMeasureKey}`}
            idealMeasurement={
              userDetailsState.idealGarmentMeasurements[eachMeasureKey as TeeMeasureLabel] || 0
            }
            initialMeasure={garmentObjectState[eachMeasureKey as TeeMeasureLabel] || 0}
          />
        ))}
      </FormContainer>
      <br />
      <Button
        variant='contained'
        color='secondary'
        sx={{ display: 'block', margin: 'auto', width: '300px' }}
        onClick={() => {
          addLog('make these my ideal measurements button clicked', location.pathname);
          dispatchUserDetailsState({
            type: 'update from ideal measurements',
            payload: {
              ...garmentObjectState,
            },
          });
        }}
      >
        Make these my ideal measurements
      </Button>
      <br />
      <ReturnButton />
    </PageContainer>
  );
}

export default ComparisonSlidersSection;
